import { Input, InputNumber, Space, Typography, Button } from 'antd';
import { useEffect, useState } from 'react';
import { IconFilter, IconRefresh } from '@tabler/icons-react';

/**
 * 申报案过滤器 — subject_id / policy_pack_id / declare_year。
 *
 * UX：本地 draft → onBlur / Enter / 「应用」按钮 commit；同值短路防 URL/queryKey 抖动。
 * 与 PeriodSelector 同范式（M3-ρ-1）。
 *
 * 后端无 UUID 校验（参数错走 RLS 空集），前端宽松：允许任意 a-z0-9- 8~64 长字符串。
 */
export interface CaseFiltersValue {
  subject_id: string;
  policy_pack_id: string;
  declare_year: number | null;
}

export interface CaseFiltersProps {
  value: CaseFiltersValue;
  onChange: (next: CaseFiltersValue) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
}

const ID_RE = /^[a-z0-9-]{8,64}$/i;
function validId(s: string): boolean {
  return s === '' || ID_RE.test(s);
}

export function CaseFilters({
  value,
  onChange,
  onRefresh,
  refreshing,
}: CaseFiltersProps) {
  const [subject, setSubject] = useState(value.subject_id);
  const [pack, setPack] = useState(value.policy_pack_id);
  const [year, setYear] = useState<number | null>(value.declare_year);

  // M3-ρ-1 high#2 教训：外部 value 变（如 URL 切换） → 同步本地 draft
  useEffect(() => {
    setSubject(value.subject_id);
  }, [value.subject_id]);
  useEffect(() => {
    setPack(value.policy_pack_id);
  }, [value.policy_pack_id]);
  useEffect(() => {
    setYear(value.declare_year);
  }, [value.declare_year]);

  const subjectValid = validId(subject.trim());
  const packValid = validId(pack.trim());

  const commit = () => {
    const next: CaseFiltersValue = {
      subject_id: subjectValid ? subject.trim() : value.subject_id,
      policy_pack_id: packValid ? pack.trim() : value.policy_pack_id,
      declare_year: year,
    };
    // 同值短路
    if (
      next.subject_id === value.subject_id &&
      next.policy_pack_id === value.policy_pack_id &&
      next.declare_year === value.declare_year
    ) {
      return;
    }
    onChange(next);
  };

  const reset = () => {
    setSubject('');
    setPack('');
    setYear(null);
    onChange({ subject_id: '', policy_pack_id: '', declare_year: null });
  };

  return (
    <Space size={10} wrap align="center">
      <IconFilter
        size={16}
        style={{ color: 'var(--color-text-tertiary)' }}
      />
      <Typography.Text
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
        }}
      >
        筛选
      </Typography.Text>

      <Input
        size="small"
        placeholder="subject_id"
        value={subject}
        status={subjectValid ? undefined : 'error'}
        onChange={(e) => setSubject(e.target.value)}
        onPressEnter={commit}
        onBlur={commit}
        allowClear
        style={{ width: 200 }}
      />
      <Input
        size="small"
        placeholder="policy_pack_id"
        value={pack}
        status={packValid ? undefined : 'error'}
        onChange={(e) => setPack(e.target.value)}
        onPressEnter={commit}
        onBlur={commit}
        allowClear
        style={{ width: 200 }}
      />
      <InputNumber
        size="small"
        placeholder="年度"
        value={year ?? undefined}
        min={2000}
        max={2100}
        onChange={(v) => setYear(v == null ? null : Number(v))}
        onPressEnter={commit}
        onBlur={commit}
        style={{ width: 100 }}
      />
      <Button size="small" onClick={reset}>
        清空
      </Button>
      {onRefresh && (
        <Button
          size="small"
          icon={<IconRefresh size={14} />}
          onClick={onRefresh}
          loading={refreshing}
        >
          刷新
        </Button>
      )}
    </Space>
  );
}
